import { inject, Injectable } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { TAILWIND_BREAKPOINTS } from '../constants/breakpoints';

/**
 * Reactive screen-size observables that mirror Tailwind's responsive prefixes 1:1.
 *
 * Breakpoint values come from `libs/shared/src/lib/constants/breakpoints.js`
 * (the same file that feeds `tailwind.config.js`), so a TS check like
 * `isMobile$ | async` always agrees with the matching `md:` class in templates.
 *
 * Each `<key>$` observable is mobile-first: it emits `true` when the viewport
 * is greater than or equal to the breakpoint, exactly like Tailwind's
 * `min-width` queries.
 */
@Injectable({
  providedIn: 'root'
})
export class BreakpointService {
  readonly #breakpointObserver = inject(BreakpointObserver);

  readonly sm$ = this.#observeMin(TAILWIND_BREAKPOINTS.sm);
  readonly md$ = this.#observeMin(TAILWIND_BREAKPOINTS.md);
  readonly lg$ = this.#observeMin(TAILWIND_BREAKPOINTS.lg);
  readonly xl$ = this.#observeMin(TAILWIND_BREAKPOINTS.xl);
  readonly xxl$ = this.#observeMin(TAILWIND_BREAKPOINTS['2xl']);
  readonly xxxl$ = this.#observeMin(TAILWIND_BREAKPOINTS['3xl']);

  /** Viewport is below `md` (< 768px). Equivalent to the CSS `md:hidden` boundary. */
  readonly isMobile$ = this.#observeMax(TAILWIND_BREAKPOINTS.md);

  /** Viewport is below `lg` (< 1024px). Useful for "mobile or tablet" UX. */
  readonly isMobileOrTablet$ = this.#observeMax(TAILWIND_BREAKPOINTS.lg);

  /** Viewport is at least `lg` (>= 1024px). Alias for `lg$`. */
  readonly isDesktop$ = this.lg$;

  #observeMin(pixels: number): Observable<boolean> {
    return this.#breakpointObserver.observe(`(min-width: ${pixels}px)`).pipe(
      map(state => state.matches),
      shareReplay(1)
    );
  }

  #observeMax(pixels: number): Observable<boolean> {
    // Use the 0.02px offset convention so `min` and `max` queries do not overlap.
    return this.#breakpointObserver.observe(`(max-width: ${pixels - 0.02}px)`).pipe(
      map(state => state.matches),
      shareReplay(1)
    );
  }
}
